import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fetchProjectProblems, fetchAllProjectProblems, approveProblems } from './ocrApi';
import { applyRowPatch } from './reviewRowPatch';

/**
 * OCR 문제 목록 + 검수 화면의 상태를 한곳에 모은 훅.
 *
 * 검수는 ④ 단계의 소유물이 아니다 — 매칭 직후 확인, AI 결과 확인, 승격 전 최종 확인으로
 * 여러 단계에서 열리는 횡단 도구다. 그런데 상태가 ④ 본문 안에 있으면 ④가 접힐 때
 * 통째로 언마운트되어, 다른 단계에서 열려면 ④를 먼저 펼쳐야 했다(그 우회가 사용자 눈에
 * "왜 ④가 펼쳐지지?"로 보였다). 그래서 상태를 패널 최상위로 올리고 여기에 담는다.
 *
 * 이 훅이 쥐는 것: 목록(항목·필터·페이지) · 검수 대상 · 확인 표시 · 전체 승인.
 * 화면은 목록을 스스로 부르지 않는다 — 두 군데서 부르면 서로 어긋난다.
 *
 * @param {object}  o
 * @param {string|number} o.projectId
 * @param {number}  o.reloadKey  매칭·분석 완료 등으로 목록을 다시 불러야 할 때 증가
 * @param {boolean} o.enabled    매칭 완료(status>=300) 전에는 조회하지 않는다 — 문제가 아직 없다
 * @param {boolean} o.closed     프로젝트 종료 — 전체 승인을 막는다
 * @param {function} o.onMutated 저장·삭제·전체 승인으로 서버 상태가 바뀐 뒤 호출 — 부모가
 *                               프로젝트 상세와 분석 현황을 다시 읽는다. 이 목록만 새로 읽으면
 *                               단계 판정이 승인 전 숫자를 계속 본다(재검수 대기가 0이 돼도
 *                               배지가 안 옮겨간다).
 *                               한 건 승인·반려는 여기를 지나지 않는다 — 목록을 다시 읽지 않고
 *                               그 줄만 갈아끼우고(patchItem), 집계는 부모가 따로 읽는다.
 */

const PER_PAGE = 20;

// 필터 → 목록 API 조건. load()와 전체 승인이 같은 모집단을 보게 하려면 한 곳에서 만들어야 한다.
// 이게 두 벌로 갈라져 있어서, '검수대기'만 보면서 누른 전체 승인이 프로젝트 전체를 대상으로 삼았다.
function filterToOpts(filterKey) {
  if (filterKey === 'pending') return { reviewStatus: 100 };
  if (filterKey === 'flagged') return { hasFlags: true };
  if (filterKey === 'conflict') return { matchStatus: 400 };
  return {};
}

export default function useOcrProblemList({
  projectId, reloadKey = 0, enabled = true, closed = false, onMutated = null,
}) {
  const { getAccessToken } = useAuth();

  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedProbId, setSelectedProbId] = useState(null); // 검수 모달 대상 — null이면 닫힘
  const reqSeqRef = useRef(0); // 필터/페이지 빠른 전환 시 이전 응답이 최신 목록을 덮지 않도록 최신 요청만 반영

  // 이번에 눈으로 확인하고 넘어간 문제 — 검수 모달에서 Enter(또는 '저장하고 다음')로 넘긴 것만 들어온다.
  // ←→ 로 그냥 지나친 것은 들어오지 않는다. 여러 건을 훑는 동안 어디까지 봤는지가 화면에 남아야 하기 때문이다.
  // 서버에 그런 필드가 없어 브라우저 세션에만 둔다 — 검수 상태(검수대기/완료/반려)와는 다른 것이라
  // 배지 모양도 다르게 하고, 탭을 닫으면 사라진다.
  const confirmKey = `ocrhub:checked:${projectId}`;
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(confirmKey);
      setCheckedIds(new Set(raw ? JSON.parse(raw) : []));
    } catch (e) {
      setCheckedIds(new Set());
    }
  }, [confirmKey]);
  const markChecked = useCallback((id) => {
    setCheckedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev).add(id);
      try { sessionStorage.setItem(confirmKey, JSON.stringify([...next])); } catch (e) { /* 세션 저장 실패는 무시 */ }
      return next;
    });
  }, [confirmKey]);
  const clearChecked = useCallback(() => {
    setCheckedIds(new Set());
    try { sessionStorage.removeItem(confirmKey); } catch (e) { /* 무시 */ }
  }, [confirmKey]);

  const load = useCallback(async (pageNum, filterKey) => {
    // 매칭 전에는 조회하지 않는다 — 문제가 아직 만들어지지 않아 빈 목록만 돌아온다.
    // 이 훅은 패널이 열리는 동안 계속 살아 있으므로, 막지 않으면 셋업 구간 내내 헛 요청이 나간다.
    if (!enabled) {
      setItems([]);
      setTotalItems(0);
      setTotalPages(1);
      setLoading(false);
      setLoadError(null);
      return null;
    }
    const seq = (reqSeqRef.current += 1);
    setLoading(true);
    setLoadError(null);
    try {
      const opts = { page: pageNum, perPage: PER_PAGE, ...filterToOpts(filterKey) };
      const d = await fetchProjectProblems(projectId, opts, getAccessToken);
      if (seq !== reqSeqRef.current) return null; // stale 응답 무시
      if (pageNum > 1 && d.items.length === 0 && pageNum > (d.totalPages || 0)) {
        // 마지막 페이지의 유일 항목이 필터에서 해소되는 등 페이지가 범위 밖으로 밀리면
        // 마지막 유효 페이지로 재조회(빈 페이지 dead-end 방지, R6)
        return load(Math.max(1, d.totalPages || 1), filterKey);
      }
      setItems(d.items);
      setPage(d.page || pageNum);
      setTotalPages(d.totalPages || 1);
      setTotalItems(d.totalItems || d.items.length);
      return d.items; // 페이지 경계 이동이 새 페이지의 첫/마지막 문제를 골라야 한다(stepPage)
    } catch (e) {
      if (seq !== reqSeqRef.current) return null;
      setItems([]);
      setTotalPages(1);
      setTotalItems(0);
      setLoadError(e.message || '문제 목록을 불러오지 못했습니다.');
      return null;
    } finally {
      if (seq === reqSeqRef.current) setLoading(false);
    }
  }, [projectId, getAccessToken, enabled]);

  // 필터 변경·마운트 시 1페이지 로드. enabled가 false→true(매칭 완료)로 바뀌어도 load가
  // 새로 만들어지므로 여기서 이어받는다.
  useEffect(() => {
    load(1, filter);
  }, [load, filter]);

  // 매칭·분석 완료(reloadKey) 재조회는 현재 페이지 유지 — 1페이지로 튀며 모달 내비 앵커가
  // 오염되는 것 방지(R5).
  const pageRef = useRef(1);
  useEffect(() => { pageRef.current = page; }, [page]);
  const prevReloadKeyRef = useRef(reloadKey);
  useEffect(() => {
    if (prevReloadKeyRef.current === reloadKey) return;
    prevReloadKeyRef.current = reloadKey;
    load(pageRef.current, filter);
  }, [reloadKey, load, filter]);

  const reload = useCallback(() => load(pageRef.current, filter), [load, filter]);

  // ── 검수 모달의 이전/다음이 페이지 경계를 넘게 한다 ──
  // 모달은 현재 페이지에 로드된 items만 본다. 양끝에서 더 갈 곳이 없으면 여기서 인접 페이지를
  // 불러오고, 이전이면 그 페이지의 마지막 문제 / 다음이면 첫 문제를 이어서 연다.
  const [navPending, setNavPending] = useState(false);
  const navSeqRef = useRef(0); // 로딩 중 모달을 닫으면 늦게 온 응답이 모달을 되살리지 않도록
  const stepPage = useCallback(async (delta) => {
    const target = page + delta;
    if (navPending || target < 1 || target > totalPages) return;
    const seq = (navSeqRef.current += 1);
    setNavPending(true);
    try {
      const newItems = await load(target, filter);
      if (seq !== navSeqRef.current) return; // 그 사이 모달을 닫았거나 다른 이동이 시작됨
      if (Array.isArray(newItems) && newItems.length > 0) {
        // 실패(null)면 아무것도 열지 않는다 — 목록 오류는 모달 안에서 토스트로 알린다(listError)
        setSelectedProbId((delta < 0 ? newItems[newItems.length - 1] : newItems[0]).ocr_prob_id);
      }
    } finally {
      if (seq === navSeqRef.current) setNavPending(false);
    }
  }, [page, navPending, totalPages, load, filter]);

  const closeReview = useCallback(() => {
    navSeqRef.current += 1; // 진행 중인 페이지 이동을 버린다
    setNavPending(false);
    setSelectedProbId(null);
  }, []);

  // 이미 보던 문제가 있으면 다시 열 때 그 자리로 돌아간다(닫았다 열며 자리를 잃지 않도록)
  const lastOpenedRef = useRef(null);
  useEffect(() => { if (selectedProbId != null) lastOpenedRef.current = selectedProbId; }, [selectedProbId]);

  // 프로젝트가 바뀌면 검수 대상과 필터를 버린다. 이 훅은 패널과 함께 살아 있어
  // 프로젝트만 갈아끼워도 언마운트되지 않는다 — 남은 문제 id는 새 프로젝트에 없는 문제다.
  useEffect(() => {
    setSelectedProbId(null);
    lastOpenedRef.current = null;
    setFilter('all');
  }, [projectId]);

  // 목록이 준비된 뒤 첫 문제를 여는 예약 — 필터를 바꿔 열거나, 목록이 아직 로딩 중일 때.
  const [pendingOpenAfterLoad, setPendingOpenAfterLoad] = useState(false);
  useEffect(() => {
    if (!pendingOpenAfterLoad || loading) return;
    setPendingOpenAfterLoad(false);
    setSelectedProbId(items[0]?.ocr_prob_id ?? null);
  }, [pendingOpenAfterLoad, loading, items]);

  // ── 목록 밖에서 특정 문제를 여는 경로(인덱스 지도 클릭) ──
  // 모달의 이전/다음·삭제 후 이동은 현재 페이지 items로만 계산된다. 목록에 없는 문제를 그냥 열면
  // 그 계산이 앵커 폴백으로 떨어져 무관한 문제로 튄다 — 그래서 그 문제가 있는 페이지로 목록을
  // 맞춘 뒤 연다. 필터도 'all'로 되돌린다(다른 필터의 모집단에는 그 문제가 아예 없을 수 있다).
  // allIndex = 필터 없는 전체 순서에서의 위치(지도가 전량을 읽어 알고 있다).
  const [pendingOpenById, setPendingOpenById] = useState(null); // { id, page, tried }
  const openById = useCallback((id, allIndex = null) => {
    if (id == null) return;
    if (items.some((p) => String(p.ocr_prob_id) === String(id))) {
      setSelectedProbId(id); // 이미 보고 있는 페이지에 있다 — 그대로 연다
      return;
    }
    const targetPage = Number.isInteger(allIndex) && allIndex >= 0
      ? Math.floor(allIndex / PER_PAGE) + 1
      : 1;
    setPendingOpenById({ id, page: targetPage, tried: false });
    if (filter !== 'all') setFilter('all');
  }, [items, filter]);

  useEffect(() => {
    if (!pendingOpenById || loading) return;
    const { id, page: want, tried } = pendingOpenById;
    const here = items.some((p) => String(p.ocr_prob_id) === String(id));
    // 목표 페이지에 도달했거나(또는 없거나) 한 번 시도했으면 더 끌지 않고 연다 —
    // 상세는 id로 읽으므로 열리기는 한다(내비만 앵커 폴백이 된다).
    if (here || tried || page === want) {
      setPendingOpenById(null);
      setSelectedProbId(id);
      return;
    }
    setPendingOpenById({ id, page: want, tried: true }); // 재시도 루프 차단
    load(want, 'all');
  }, [pendingOpenById, loading, items, page, load]);

  // 검수 화면을 여는 유일한 경로. 어느 단계에서 불러도 그 단계를 펼치지 않는다 —
  // 모달은 패널 최상위에 있고 이 훅의 상태만 본다.
  const openReview = useCallback((opts = {}) => {
    if (opts.filter && opts.filter !== filter) {
      setPendingOpenAfterLoad(true); // 필터가 바뀌면 목록이 다시 온다 — 그 결과의 첫 문제를 연다
      setFilter(opts.filter);
      return;
    }
    if (loading || items.length === 0) {
      setPendingOpenAfterLoad(true); // 로딩이 끝나면 첫 문제를 연다(끝났는데 0건이면 열지 않는다)
      return;
    }
    const stillThere = items.some((p) => String(p.ocr_prob_id) === String(lastOpenedRef.current));
    setSelectedProbId(stillThere ? lastOpenedRef.current : items[0]?.ocr_prob_id ?? null);
  }, [filter, loading, items]);

  // ── 전체 승인 ──
  // 대상은 '지금 필터에 걸린 것 전부'다. 화면에 보이는 20건만 승인하면 페이지를 넘겨 가며
  // 같은 버튼을 반복해야 하므로, 필터 조건 그대로 전량을 받아 훑는다.
  // 이미 검수완료된 것과 승격된 것은 뺀다 — 전자는 할 일이 없고 후자는 백엔드가 423으로 막는다.
  const [approving, setApproving] = useState(null); // { done, total } | null
  const [approveResult, setApproveResult] = useState(null);
  const approveAll = useCallback(async () => {
    if (approving || closed) return;
    setApproveResult(null);
    setApproving({ done: 0, total: 0 });
    try {
      const { items: all } = await fetchAllProjectProblems(projectId, getAccessToken, filterToOpts(filter));
      // 반려(400)는 사람이 '승격 대상에서 뺀다'고 내린 결정이다 — 버튼 하나로 되살리지 않는다.
      // 게다가 반려는 AI 생성분을 지우고 analysis_status를 100으로 되돌리므로, 되살리면
      // 그 문제가 다시 분석 대상이 되어 LLM 비용까지 따라온다. 되살리려면 그 문제를 열어야 한다.
      // 승격분은 백엔드가 423으로 막는다.
      // 승격된 행의 검수 변경은 2026-08-30 에 열렸지만 대상에서 계속 뺀다 — 재등록을 하지 않으므로
      // 승인해도 문제은행에 가지 않는다. 계약이 막아서가 아니라 뜻이 없어서다.
      const targets = all.filter((p) => p.review_status === 100 && p.promoted !== true);
      if (targets.length === 0) {
        setApproveResult({ text: '승인할 문제가 없습니다 — 검수대기 상태인 문제만 대상입니다(반려·검수완료·등록된 문제 제외).', tone: 'gray' });
        return;
      }
      // 되돌리기 어려운 대량 작업이라 건수를 보여주고 한 번 묻는다
      if (!window.confirm(`${targets.length}건을 한꺼번에 승인합니다. 진행할까요?`)) return;
      setApproving({ done: 0, total: targets.length });
      const res = await approveProblems(
        targets.map((p) => p.ocr_prob_id),
        getAccessToken,
        (done, total) => setApproving({ done, total }),
      );
      if (res.aborted) return;
      setApproveResult(
        res.rejected.length > 0
          ? { text: `${res.done}건 승인 · ${res.rejected.length}건 거부됨(차단 플래그가 남아 있는 문제입니다)`, tone: 'amber' }
          : { text: `${res.done}건을 모두 승인했습니다.`, tone: 'green' },
      );
      await load(pageRef.current, filter);
      if (onMutated) await onMutated();
    } catch (e) {
      setApproveResult({ text: e.message || '전체 승인에 실패했습니다.', tone: 'red' });
    } finally {
      setApproving(null);
    }
  }, [approving, closed, filter, projectId, getAccessToken, load, onMutated]);

  // 모달의 저장·삭제 뒤에는 목록을 다시 읽는다 — 본문·인덱스가 바뀌면 미리보기와 순서까지 달라져
  // 한 줄만 갈아끼울 수 없다.
  // 진행 통계는 프로젝트 상세에 실려 오므로 onMutated(=refreshDetail)가 함께 갱신한다.
  const reloadAfterMutate = useCallback(async () => {
    await load(pageRef.current, filter);
    if (onMutated) await onMutated();
  }, [load, filter, onMutated]);

  // 승인·반려는 그 한 줄만 갈아끼운다 — 목록을 다시 읽지 않는다.
  const patchItem = useCallback((id, patch) => {
    if (id == null || !patch) return;
    setItems((prev) => applyRowPatch(prev, id, patch));
  }, []);

  return {
    // 목록
    items, loading, loadError, page, totalPages, totalItems, filter,
    setFilter, load, reload, reloadAfterMutate, patchItem,
    goToPage: useCallback((n) => load(n, filter), [load, filter]),
    // 검수 화면
    selectedProbId, openReview, openById, closeReview, navPending, stepPage,
    select: setSelectedProbId,
    // 확인 표시
    checkedIds, markChecked, clearChecked,
    // 전체 승인
    approving, approveResult, setApproveResult, approveAll,
  };
}
